// DraggableResizableStream.tsx
import React, { useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { FabricImage } from "fabric";
import * as fabric from "fabric";
import { useAtomValue } from "jotai";
import useRecorderContext from "../context/useRecorderContext";
import { useVideoTicker, isDirectRenderActive } from "../hooks/useVideoTicker";
import useGuideLineHook from "../hooks/useGuideLineHook";
import {
  LayoutPreset,
  TGuideLines,
  getPadding,
} from "../presets/layoutPresets";
import { guideColor } from "../constants/common";
import { createGuideLine } from "../utils/common";
import { ratioToNumber } from "../utils/ratio";
import { useUserStore } from "../hooks/useUserStore";
import { isRatioTransitioningAtom, canvasTransitionSnapshotAtom } from "../store/canvasStore";
import { store } from "../store/canvasStoreInstance";

interface Props {
  videoElement: HTMLVideoElement;
  initialX: number;
  initialY: number;
  initialWidth: number;
  initialHeight: number;
  isSelected: boolean;
  streamType: "screen" | "webcam";
  onPositionChange?: (
    streamType: "screen" | "webcam",
    position: { x: number; y: number; width: number; height: number }
  ) => void;
  isManualPreset?: boolean;
  getCurrentLayout: () => { stableLayout: LayoutPreset; isRecording: boolean };
}
type CanvasRatio = "16:9" | "1:1" | "9:16";

// Pulls one frame from the video MediaStreamTrack and forces a Fabric paint.
async function forceFirstFrameWithImageCapture(
  videoEl: HTMLVideoElement,
  videoImg: FabricImage,
  canvas: any,
  streamType: string
) {
  try {
    if (streamType === "webcam") return;
    const ms = videoEl.srcObject as MediaStream | null;
    const track = ms?.getVideoTracks?.()[0];
    if (!track || track.readyState !== "live" || !(window as any).ImageCapture)
      return;

    const ic = new (window as any).ImageCapture(track);
    const bmp: ImageBitmap = await ic.grabFrame();

    const snap = document.createElement("canvas");
    snap.width = bmp.width;
    snap.height = bmp.height;
    const sctx = snap.getContext("2d");
    if (sctx) sctx.drawImage(bmp, 0, 0);

    (videoImg as any).setElement(snap);
    (videoImg as any).dirty = true;
    canvas.renderAll();

    const swapBack = () => {
      (videoImg as any).setElement(videoEl);
      (videoImg as any).dirty = true;
      canvas.renderAll();
    };

    const once = { once: true as const };
    videoEl.addEventListener("playing", swapBack, once);
    videoEl.addEventListener("loadeddata", swapBack, once);
    if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
      (videoEl as any).requestVideoFrameCallback(() => swapBack());
    }
  } catch {}
}

const DraggableResizableStreamImpl: React.FC<Props> = ({
  videoElement,
  initialX,
  initialY,
  initialWidth,
  initialHeight,
  isSelected,
  streamType,
  onPositionChange,
  isManualPreset,
  getCurrentLayout,
}) => {
  const {
    roundedWebcam,
    mirroredWebcam,
    avatarWebcam,
    avatarImage,
    avatarImageLoaded,
    preloadAvatarImage,
    stageRef,
    canvasReady,
    canvasWidth,
    canvasHeight,
    canvasRatio,
    recordingQuality, // eslint-disable-line @typescript-eslint/no-unused-vars
    isRecording,
    showCountdown,
    setHasManualModifications,
    clickedOutside,
    setClickedOutside,
    selectedStream,
    setSelectedStream,
    edgeToEdge,
    // MSTG integration
    mstgWriteFrame,
    isMSTGActive,
    registerResetMSTGFrameCount,
    // Canvas capture stream frame request (for captureStream(0))
    requestCanvasFrame,
  } = useRecorderContext();

  // Simplified avatar logic - swap source on existing FabricImage object
  const lastAvatarWebcamRef = useRef(avatarWebcam);
  const lastAvatarImageRef = useRef(avatarImage);

  // Capture check: capture when recording is active
  // For MSTG: check if writer exists and track is live
  // For captureStream(0): check if recording is active
  const shouldCaptureFrame = React.useCallback(() => {
    // If MSTG is enabled and active, use MSTG
    if (mstgWriteFrame && isMSTGActive()) {
      return true;
    }
    // For captureStream(0), capture when recording
    return isRecording;
  }, [mstgWriteFrame, isMSTGActive, isRecording]);

  const { registerVideoObject, unregisterVideoObject, resetFrameCount } =
    useVideoTicker(stageRef, {
      // Capture check for MSTG or captureStream(0)
      shouldCaptureFrame,
      // Write frames to MSTG track when available
      onFrameRendered: mstgWriteFrame ?? undefined,
      // Request frame from captureStream(0) - triggers actual capture for hidden tab
      requestCanvasFrame,
    });
    
  // Register the resetFrameCount function with context so it's called when recording starts
  React.useEffect(() => {
    if (registerResetMSTGFrameCount && resetFrameCount) {
      registerResetMSTGFrameCount(resetFrameCount);
    }
  }, [registerResetMSTGFrameCount, resetFrameCount]);

  const { userData } = useUserStore();

  const videoRef = useRef<FabricImage | null>(null);
  const frameRef = useRef<any | null>(null);
  const clipRef = useRef<any | null>(null); // Rect or Circle
  // Avatar image is now managed by context to avoid reset on layout changes
  const guideLinesRef = useRef<TGuideLines>({
    left: null,
    right: null,
    top: null,
    bottom: null,
    centerX: null,
    centerY: null,
    objectLeft: null,
    objectRight: null,
    objectTop: null,
    objectBottom: null,
    objectCenterX: null,
    objectCenterY: null,
    leftEdge: null,
    rightEdge: null,
    topEdge: null,
    bottomEdge: null,
    squareH: null,
    squareV: null,
  });

  const initRef = useRef(false);
  const initOnce = useRef(false);
  const interactingRef = useRef(false);
  const lastRatioRef = useRef<CanvasRatio | null>(null);
  const lastRatioARef = useRef<CanvasRatio | null>(null);
  
  // Refs for fade-in animation cleanup - prevents leaked timers on remount
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRafRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Watch isRatioTransitioningAtom to immediately hide objects when ratio changes
  const isRatioTransitioning = useAtomValue(isRatioTransitioningAtom);
  
  // CRITICAL: Immediately hide existing objects when ratio transition starts
  // This prevents the distortion "blink" where old layout is visible in new canvas dimensions
  useEffect(() => {
    if (videoRef.current && frameRef.current) {
      const canvas = localCanvasRef.current || stageRef?.current;
      if (isRatioTransitioning) {
        // Hide objects immediately when transition starts
        videoRef.current.set({ opacity: 0 });
        frameRef.current.set({ opacity: 0 });
        if (canvas && typeof canvas.requestRenderAll === 'function') {
          canvas.requestRenderAll();
        }
      }
      // Note: When isRatioTransitioning becomes false, don't restore here
      // The new component's fade-in handles visibility for fresh mounts
      // For non-remounting cases, the flag being cleared means transition is complete
    }
  }, [isRatioTransitioning, streamType, stageRef]);

  const prevCanvasRef = useRef<any | null>(null);
  // CRITICAL FIX: Store canvas ref locally so it's available during cleanup even if stageRef becomes null
  const localCanvasRef = useRef<any | null>(null);
  const lastCanvasDimensionsRef = useRef<{
    width: number;
    height: number;
  } | null>(null);

  const currentSourceRef = useRef<"webcam" | "avatar">("webcam");

  const ratioNum = React.useMemo(
    () => ratioToNumber(canvasRatio),
    [canvasRatio]
  );
  const isPortrait = ratioNum < 1;
  const isRecordingRef = useRef(isRecording);
  const showCountdownRef = useRef(showCountdown);
  const roundedWebcamRef = useRef(roundedWebcam);
  const mirroredWebcamRef = useRef(mirroredWebcam);
  const isSelectedRef = useRef(isSelected);
  const canvasWidthRef = useRef(canvasWidth);
  const canvasHeightRef = useRef(canvasHeight);

  // Update current source ref when avatarWebcam changes
  useEffect(() => {
    currentSourceRef.current = avatarWebcam ? "avatar" : "webcam";
  }, [avatarWebcam]);

  // Update isSelected ref when isSelected changes
  useEffect(() => {
    isSelectedRef.current = isSelected;
  }, [isSelected]);

  // Update canvas dimension refs when dimensions change
  useEffect(() => {
    canvasWidthRef.current = canvasWidth;
    canvasHeightRef.current = canvasHeight;
  }, [canvasWidth, canvasHeight]);

  const clickedOutsideRef = useRef(clickedOutside);
  // ---- Minimum size (16% of canvas) ----
  const MIN_SIZE_PCT = 0.16;
  const constraintActiveRef = useRef(false);

  // at top with other refs
  const feedbackOutlineRef = useRef<any | null>(null);

  const showFeedbackOutline = (
    canvas: any,
    shape: "circle" | "rect",
    cx: number,
    cy: number,
    w: number,
    h: number
  ) => {
    const strokeWidth = getAdjustedControlsSize(1); // Consistent stroke width for feedback outline
    let outline = feedbackOutlineRef.current;

    const common = {
      fill: "transparent",
      stroke: "#FF3B30",
      strokeWidth,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      name: "min-constraint-feedback",
    } as const;

    if (!outline) {
      outline =
        shape === "circle"
          ? new fabric.Circle({
              ...common,
              radius: Math.min(w, h) / 2,
              left: cx,
              top: cy,
              originX: "center",
              originY: "center",
            })
          : new fabric.Rect({
              ...common,
              width: w,
              height: h,
              left: cx,
              top: cy,
              originX: "center",
              originY: "center",
            });
      feedbackOutlineRef.current = outline;
      canvas.add(outline);
    } else {
      if (shape === "circle" && outline instanceof fabric.Circle) {
        outline.set({
          radius: Math.min(w, h) / 2,
          left: cx,
          top: cy,
          originX: "center",
          originY: "center",
          strokeWidth,
        });
      } else if (shape === "rect" && outline instanceof fabric.Rect) {
        outline.set({
          width: w,
          height: h,
          left: cx,
          top: cy,
          originX: "center",
          originY: "center",
          strokeWidth,
        });
      } else {
        canvas.remove(outline);
        const newOutline =
          shape === "circle"
            ? new fabric.Circle({
                ...common,
                radius: Math.min(w, h) / 2,
                left: cx,
                top: cy,
                originX: "center",
                originY: "center",
              })
            : new fabric.Rect({
                ...common,
                width: w,
                height: h,
                left: cx,
                top: cy,
                originX: "center",
                originY: "center",
              });
        feedbackOutlineRef.current = newOutline;
        canvas.add(newOutline);
      }
    }
    (canvas as any).bringObjectToFront(outline!);

    canvas.requestRenderAll();
  };

  const hideFeedbackOutline = (canvas?: any) => {
    const o = feedbackOutlineRef.current;
    if (o && canvas) canvas.remove(o);
    feedbackOutlineRef.current = null;
  };

  // Store original bbox & corner for current scaling gesture
  const scalingSessionRef = useRef<{
    corner: string | null;
    origLeft: number;
    origTop: number;
    origRight: number;
    origBottom: number;
    origWidth: number;
    origHeight: number;
  } | null>(null);

  const getMinSizePx = useCallback(() => {
    const canvas: any | undefined = stageRef?.current?.getCanvas?.();
    if (!canvas) return { minW: 0, minH: 0 };

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    // Calculate minimum size based on the actual canvas dimensions (not scaled)
    const minW = Math.min(canvasWidth, canvasHeight) * MIN_SIZE_PCT;
    const minH = Math.min(canvasWidth, canvasHeight) * MIN_SIZE_PCT;

    // Calculate display scale factor based on canvas ratio
    // For portrait mode (9:16), the scale calculation is different
    let displayScale = 1;
    if (canvasRatio === "9:16") {
      // In portrait mode, the canvas is scaled differently
      // Use a conservative scale factor that ensures minimum size is reasonable
      displayScale = Math.min(1, 720 / Math.max(canvasWidth, canvasHeight));
    } else {
      // For landscape and square, use a standard scale factor
      displayScale = Math.min(1, 1280 / Math.max(canvasWidth, canvasHeight));
    }

    // Apply display scale to get the visible size
    const displayMinW = minW / displayScale;
    const displayMinH = minH / displayScale;

    // Ensure minimum size is at least the current fixed values, adjusted for display
    const absoluteMinW = Math.max(displayMinW, roundedWebcam ? 100 : 160);
    const absoluteMinH = Math.max(displayMinH, roundedWebcam ? 100 : 160);

    return {
      minW: Math.max(1, absoluteMinW),
      minH: Math.max(1, absoluteMinH),
    };
  }, [stageRef, canvasRatio, roundedWebcam]);

  const getCanvasDisplayScale = useCallback(() => {
    const canvas = stageRef?.current?.getCanvas?.();
    if (!canvas) return 2;
    const backingW = canvas.getWidth();
    const el = canvas.lowerCanvasEl || canvas.upperCanvasEl;
    const cssW = el?.getBoundingClientRect()?.width;
    if (!cssW || cssW <= 0) return 2;
    return backingW / cssW;
  }, [stageRef]);

  const getAdjustedControlsSize = useCallback(
    (screenPx: number) => {
      return screenPx * getCanvasDisplayScale();
    },
    [getCanvasDisplayScale]
  );

  const getCornerSize = useCallback(() => {
    if (isRecordingRef.current || showCountdownRef.current) {
      return 0;
    }
    return 12 * getCanvasDisplayScale();
  }, [getCanvasDisplayScale]);


  const getBorderScaleFactor = useCallback(() => {
    return 1.5 * getCanvasDisplayScale();
  }, [getCanvasDisplayScale]);


  const getGuideStrokeWidth = useCallback(
    (guideType: "main" | "object" | "edge") => {
      const screenPx = { main: 1.5, object: 2, edge: 1.5 };
      return screenPx[guideType] * getCanvasDisplayScale();
    },
    [getCanvasDisplayScale]
  );

  // Slight rounding for webcam when not fully rounded
  const getCornerRadius = useCallback(
    (w: number, h: number, rounded: boolean) => {
      // When edge-to-edge is enabled, use sharp corners (0 radius)
      if (edgeToEdge) return 0;
      if (rounded) return Math.min(w, h) / 2; // full circle
      // ~6% of the smaller side, scaled; min ~8px, capped so it never looks pill-y
      const minSide = Math.min(w, h);
      const px = Math.max(getAdjustedControlsSize(8), minSide * 0.06);
      return Math.min(px, minSide / 32);
    },
    [getAdjustedControlsSize, edgeToEdge]
  );

  // Soft drop shadow for webcam video (clipped to the shape)
  const applyWebcamShadow = useCallback(
    (img: any) => {
      img.set(
        "shadow",
        new fabric.Shadow({
          color: "rgba(0,0,0,0.28)",
          blur: getAdjustedControlsSize(18),
          offsetX: getAdjustedControlsSize(2),
          offsetY: getAdjustedControlsSize(4),
          // Keep visual weight consistent while scaling
          nonScaling: true,
        })
      );
    },
    [getAdjustedControlsSize]
  );

  // Track current positions to avoid resetting during re-renders
  const currentPositionRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const ensureWebcamOnTop = (canvas: any) => {
    const objs = canvas.getObjects();
    objs
      .filter((o: any) => o.name === "webcam-video")
      .forEach((o: any) => canvas.bringObjectToFront(o));
    objs
      .filter((o: any) => o.name === "webcam-frame")
      .forEach((o: any) => canvas.bringObjectToFront(o));
    canvas.requestRenderAll();
  };

  const updateFrameForRecording = useCallback(
    (frame: any) => {
      // CRITICAL: Always keep cornerSize at actual size for hit detection
      // Only use transparentCorners to hide controls visually
      const actualCornerSize = getCornerSize();
      
      if (isRecordingRef.current || showCountdownRef.current) {
        // Completely hide all controls during recording - no resize allowed
        frame.setControlsVisibility({
          mt: false, mb: false, ml: false, mr: false,
          tl: false, tr: false, bl: false, br: false, mtr: false,
        });
        frame.set({
          selectable: false, // CRITICAL: Disable selection during recording
          evented: false, // CRITICAL: Disable events during recording
          hasBorders: false,
          hasControls: false, // Completely disable controls
          lockScalingX: true,
          lockScalingY: true,
          cornerColor: "transparent",
          cornerStrokeColor: "transparent",
          borderColor: "transparent",
          cornerSize: 0,
          transparentCorners: true,
          scaleX: 1,
          scaleY: 1,
          fill: "transparent",
          visible: false,
        });
      } else if (clickedOutsideRef.current) {
        // When clicking outside, only hide borders but preserve scale
        frame.set({
          selectable: true, // CRITICAL: Keep selectable even when clicked outside
          evented: true, // CRITICAL: Keep evented even when clicked outside
          hasBorders: false,
          hasControls: true,
          lockScalingX: false,
          lockScalingY: false,
          cornerColor: "transparent",
          cornerStrokeColor: "transparent",
          borderColor: "transparent",
          cornerSize: actualCornerSize,
          transparentCorners: true,
          visible: true,
          fill: "rgba(0,0,0,0.01)",
        });
      } else {
        // Restore controls visibility based on stream type
        const isScreen = streamType === "screen";
        frame.setControlsVisibility({
          mt: !isScreen, mb: !isScreen, ml: !isScreen, mr: !isScreen,
          tl: true, tr: true, bl: true, br: true, mtr: false,
        });
        frame.set({
          selectable: true, // CRITICAL: Restore selection after recording
          evented: true, // CRITICAL: Restore events after recording
          hasBorders: true,
          hasControls: true,
          lockScalingX: false,
          lockScalingY: false,
          cornerColor: "white",
          cornerStrokeColor: guideColor,
          borderColor: guideColor,
          cornerSize: actualCornerSize,
          transparentCorners: false,
          borderScaleFactor: getBorderScaleFactor(),
          fill: "rgba(0,0,0,0.01)",
          visible: true,
        });
      }
      frame.setCoords();
    },
    [
      getBorderScaleFactor,
      getCornerSize,
    ]
  );

  const layoutToFrame = (
    canvas: any,
    frame: any,
    videoImg: any,
    clip: any,
    vw: number,
    vh: number,
    isScreen: boolean,
    isWebcam: boolean,
    roundedWebcam: boolean,
    preserveFrameBounds: boolean = false,
    objectFit: "cover" | "fill" = "cover"
  ) => {
    let w = (frame.width ?? 0) * (frame.scaleX ?? 1);
    let h = (frame.height ?? 0) * (frame.scaleY ?? 1);
    
    // For rounded webcam, make the frame square to ensure proper circular clipping
    // But only if we're not preserving frame bounds (i.e., not called during scaling/moving)
    if (isWebcam && roundedWebcam && !preserveFrameBounds) {
      const minDimension = Math.min(w, h);
      w = minDimension;
      h = minDimension;
      frame.set({ width: w, height: h, scaleX: 1, scaleY: 1 });
    }

    // Use object-fit behavior based on parameter
    // "cover" maintains aspect ratio and fills frame (may crop)
    // "fill" stretches to fill frame completely (may distort)
    const scaleX = w / vw;
    const scaleY = h / vh;
    const scale =
      objectFit === "fill"
        ? Math.max(scaleX, scaleY) // For fill, we'll use individual scales below
        : Math.max(scaleX, scaleY); // For cover, use max to maintain aspect ratio
    const center = frame.getCenterPoint();

    if (
      lastAvatarImageRef.current &&
      currentSourceRef.current === "avatar" &&
      streamType === "webcam"
    ) {
      // Switch to avatar (only for webcam streams)

      videoImg.setElement(lastAvatarImageRef.current);
      videoImg.dirty = true;
      videoImg.set({ flipX: false }); // Avatar doesn't need mirroring

      // Use object-fit: cover behavior for avatar images
      if (frame && clip) {
        const frameWidth = (frame.width ?? 0) * (frame.scaleX ?? 1);
        const frameHeight = (frame.height ?? 0) * (frame.scaleY ?? 1);
        const center = frame.getCenterPoint();

        // Get avatar dimensions
        const avatarWidth = lastAvatarImageRef.current.width || 1;
        const avatarHeight = lastAvatarImageRef.current.height || 1;

        // Use cover behavior to maintain aspect ratio (object-fit: cover)
        const scale = Math.max(
          frameWidth / avatarWidth,
          frameHeight / avatarHeight
        );

        videoImg.set({
          left: center.x,
          top: center.y,
          originX: "center",
          originY: "center",
          scaleX: objectFit === "fill" ? scaleX : scale,
          scaleY: objectFit === "fill" ? scaleY : scale,
        });

        videoImg.setCoords();
      }
    } else {
      videoImg.setElement(videoElement);

      videoImg.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        scaleX: objectFit === "fill" ? scaleX : scale,
        scaleY: objectFit === "fill" ? scaleY : scale,
        width: vw,
        height: vh,
      });
    }

    if (isWebcam && roundedWebcam && clip instanceof fabric.Circle) {
      const radius = Math.min(w, h) / 2;
      clip.set({
        radius,
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
      } as any);
    } else if (clip instanceof fabric.Rect) {
      const cornerR = getCornerRadius(w, h, /*rounded*/ false);
      clip.set({
        width: w,
        height: h,
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        rx: cornerR,
        ry: cornerR,
      } as any);
    }
    (clip as any).absolutePositioned = true;
    (videoImg as any).clipPath = clip;

    frame.setCoords();
    videoImg.setCoords();
    clip.setCoords();
    canvas.requestRenderAll();
  };

  const { updateGuideVisibility, removeAllGuides, updateAllGuidePositions } =
    useGuideLineHook({
      guideLinesRef,
      interactingRef,
      getCurrentLayout,
      streamType,
    });

  const videoImgLayoutToFrameCallback = (callback: any) => callback();

  // —————————————— INIT + CLEANUP ——————————————
  const cleanupCanvasObjects = useCallback(() => {
    // CRITICAL: Cancel any pending fade-in animations to prevent leaked timers
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    if (fadeRafRef.current) {
      cancelAnimationFrame(fadeRafRef.current);
      fadeRafRef.current = null;
    }
    isMountedRef.current = false;
    
    // CRITICAL FIX: Use localCanvasRef instead of stageRef (which may be null during unmount)
    const canvas: any | undefined = localCanvasRef.current || stageRef?.current?.getCanvas?.();
    if (canvas) {
      const v = videoRef.current;
      const f = frameRef.current;
      const c = clipRef.current;
      
      if (v) {
        unregisterVideoObject(v);
        canvas.remove(v);
      }
      if (f) {
        canvas.remove(f);
      }
      // FIX: Remove clip object from canvas to prevent orphan rects
      if (c) {
        canvas.remove(c);
      }

      removeAllGuides(canvas);
      canvas.requestRenderAll();
    }

    videoRef.current = null;
    frameRef.current = null;
    clipRef.current = null;
    guideLinesRef.current = {
      left: null,
      right: null,
      top: null,
      bottom: null,
      centerX: null,
      centerY: null,
      objectLeft: null,
      objectRight: null,
      objectTop: null,
      objectBottom: null,
      objectCenterX: null,
      objectCenterY: null,
      leftEdge: null,
      rightEdge: null,
      topEdge: null,
      bottomEdge: null,
      squareH: null,
      squareV: null,
    };
    initRef.current = false;
    initOnce.current = false;
  }, [removeAllGuides, stageRef, unregisterVideoObject, streamType]);

  const initCanvasObjects = useCallback(() => {
    if (initOnce.current) {
      return;
    }
    if (!canvasReady || !stageRef?.current || !videoElement) {
      return;
    }
    if (
      videoElement.readyState < HTMLMediaElement.HAVE_METADATA ||
      videoElement.videoWidth === 0 ||
      videoElement.videoHeight === 0
    ) {
      return;
    }
    
    initOnce.current = true;

    // Note: Avatar image preloading is handled in the avatar creation section below
    // to avoid race conditions with multiple preload calls

    const canvas = stageRef.current.getCanvas() as any;
    
    // CRITICAL FIX: Store canvas ref locally for cleanup
    localCanvasRef.current = canvas;

    canvas.selection = true;
    canvas.skipTargetFind = false;
    canvas.perPixelTargetFind = false;
    canvas.targetFindTolerance = 8;
    (canvas as any).isDrawingMode = false;
    if (canvas.upperCanvasEl) canvas.upperCanvasEl.style.pointerEvents = "auto";

    try {
      const lowerCtx = (canvas.lowerCanvasEl.getContext("2d") ??
        (canvas as any).getContext?.()) as CanvasRenderingContext2D | null;
      if (lowerCtx && "imageSmoothingEnabled" in lowerCtx) {
        lowerCtx.imageSmoothingEnabled = false;
      }
    } catch {}

    const isScreen = streamType === "screen";
    const isWebcam = streamType === "webcam";
    const vw = videoElement.videoWidth;
    const vh = videoElement.videoHeight;

    videoElement.muted = true;
    (videoElement as any).playsInline = true;
    videoElement.play().catch(() => {});

    // CRITICAL FIX: Get backing store scale factor to convert CSS-space coordinates to Fabric coordinates
    // The backing store is at recording resolution (e.g., 1920x1080) while CSS displays at smaller size (e.g., 960x540)
    // Objects must be positioned in backing store coordinates, not CSS coordinates
    // NOTE: We no longer use setZoom() - the backing store IS the coordinate system
    const backingWidth = canvas.getWidth();
    const cssWidth = canvasWidth || 960;
    const backingScale = backingWidth / cssWidth;

    const rawPosition = currentPositionRef.current || {
      x: initialX,
      y: initialY,
      width: initialWidth,
      height: initialHeight,
    };

    // Scale position from CSS-space to backing store coordinates
    const position = {
      x: rawPosition.x * backingScale,
      y: rawPosition.y * backingScale,
      width: rawPosition.width * backingScale,
      height: rawPosition.height * backingScale,
    };

    // Build guides
    const cW = canvas.getWidth();
    const cH = canvas.getHeight();
    const cCX = cW / 2;
    const cCY = cH / 2;
    const overflowAmount = 20;

    const guideConfigs = {
      left: {
        points: [0, 0, 0, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      right: {
        points: [cW, 0, cW, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      top: {
        points: [0, 0, cW, 0] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      bottom: {
        points: [0, cH, cW, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      centerX: {
        points: [cCX, 0, cCX, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      centerY: {
        points: [0, cCY, cW, cCY] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("main"),
      },
      objectLeft: {
        points: [0, 0, 0, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      objectRight: {
        points: [0, 0, 0, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      objectTop: {
        points: [0, 0, cW, 0] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      objectBottom: {
        points: [0, cH, cW, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      objectCenterX: {
        points: [0, 0, 0, cH] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      objectCenterY: {
        points: [0, 0, cW, 0] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      leftEdge: {
        points: [0, -overflowAmount, 0, cH + overflowAmount] as [
          number,
          number,
          number,
          number
        ],
        strokeWidth: getGuideStrokeWidth("edge"),
      },
      rightEdge: {
        points: [cW, -overflowAmount, cW, cH + overflowAmount] as [
          number,
          number,
          number,
          number
        ],
        strokeWidth: getGuideStrokeWidth("edge"),
      },
      topEdge: {
        points: [-overflowAmount, 0, cW + overflowAmount, 0] as [
          number,
          number,
          number,
          number
        ],
        strokeWidth: getGuideStrokeWidth("edge"),
      },
      bottomEdge: {
        points: [-overflowAmount, cH, cW + overflowAmount, cH] as [
          number,
          number,
          number,
          number
        ],
        strokeWidth: getGuideStrokeWidth("edge"),
      },
      squareH: {
        points: [0, 0, 0, 0] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
      squareV: {
        points: [0, 0, 0, 0] as [number, number, number, number],
        strokeWidth: getGuideStrokeWidth("object"),
      },
    };

    const guides = Object.entries(guideConfigs).reduce((acc, [key, config]) => {
      acc[key as keyof TGuideLines] = createGuideLine(
        config.points,
        config.strokeWidth,
        getBorderScaleFactor()
      );
      return acc;
    }, {} as TGuideLines);
    guideLinesRef.current = guides;

    // Fabric image for <video> - using FabricImage instead of deprecated fabric.Image
    // Start with avatar if avatarWebcam is enabled and avatar is loaded
    const initialElement =
      streamType === "webcam" &&
      avatarWebcam &&
      avatarImageLoaded &&
      avatarImage
        ? avatarImage
        : videoElement;

    const videoImg = new FabricImage(initialElement, {
      originX: "center",
      originY: "center",
      selectable: false,
      evented: false,
      objectCaching: false,
      name: `${streamType}-video`,
      flipX:
        streamType === "webcam"
          ? avatarWebcam
            ? false
            : mirroredWebcam
          : false,
      hasBorders: false,
      hasControls: false,
      hoverCursor: "default",
    });

    if (isWebcam) {
      applyWebcamShadow(videoImg);
    }

    const frame = new fabric.Rect({
      left: position.x,
      top: position.y,
      width: position.width,
      height: position.height,
      fill: "rgba(0,0,0,0.01)",
      stroke: "transparent",
      strokeWidth: 0,
      selectable: true,
      evented: true,
      hasRotatingPoint: false,
      lockRotation: true,
      lockScalingFlip: true,
      lockMovementX: false,
      lockMovementY: false,
      // For circle webcam we want uniform scaling; for screen we already lock
      lockUniScaling:
        streamType === "screen" || (streamType === "webcam" && roundedWebcam),
      hasBorders: !isRecordingRef.current && !showCountdownRef.current,
      cornerColor:
        isRecordingRef.current || showCountdownRef.current
          ? "transparent"
          : "white",
      cornerStrokeColor:
        isRecordingRef.current || showCountdownRef.current
          ? "transparent"
          : guideColor,
      cornerStyle: "circle",
      cornerSize: getCornerSize(),
      transparentCorners: isRecordingRef.current || showCountdownRef.current,
      name: `${streamType}-frame`,
      borderColor:
        isRecordingRef.current || showCountdownRef.current
          ? "transparent"
          : guideColor,
      borderScaleFactor: getBorderScaleFactor(),
      strokeUniform: true,
    });

    frame.setControlsVisibility(
      streamType === "screen"
        ? {
            mt: false,
            mb: false,
            ml: false,
            mr: false,
            mtr: false,
            tl: true,
            tr: true,
            bl: true,
            br: true,
          }
        : {
            mt: true,
            mb: true,
            ml: true,
            mr: true,
            mtr: false,
            tl: true,
            tr: true,
            bl: true,
            br: true,
          }
    );

    // ---- Clip path: Circle for rounded webcam, Rect otherwise
    const makeCircleClip = (w: number, h: number) =>
      new fabric.Circle({
        radius: Math.min(w, h) / 2,
        originX: "center",
        originY: "center",
      });

    const makeRectClip = (w: number, h: number, rounded: boolean) =>
      new fabric.Rect({
        width: w,
        height: h,
        originX: "center",
        originY: "center",
        // For webcam: pass forRoundedWebcam so it becomes a circle when needed.
        // For screen: forRoundedWebcam will be false, giving a small radius.
        rx: getCornerRadius(w, h, rounded),
        ry: getCornerRadius(w, h, rounded),
      });

    const clip =
      streamType === "webcam" && roundedWebcam
        ? makeCircleClip(position.width, position.height)
        : makeRectClip(
            position.width,
            position.height,
            streamType === "webcam" && roundedWebcam
          );

    // CRITICAL: Start with opacity 0 BEFORE adding to canvas
    // This prevents the distortion from being visible during ratio transitions
    videoImg.set({ opacity: 0 });
    frame.set({ opacity: 0 });
    
    // Make clip non-interactive and invisible
    clip.set({
      selectable: false,
      evented: false,
      visible: false,
      excludeFromExport: true,
    });

    // Add to canvas (all objects already have opacity 0)
    // FIX: Do NOT add clip to canvas - Fabric.js clipPath works without being on canvas
    // Adding clip to canvas creates orphan rect objects that are never properly cleaned up
    canvas.add(videoImg);
    canvas.add(frame);

    ensureWebcamOnTop(canvas);
    canvas.setActiveObject(frame);

    // CRITICAL: Recalculate canvas offset after adding objects
    // This ensures hit detection works correctly with the zoomed viewport
    if (typeof (canvas as any).calcOffset === "function") {
      (canvas as any).calcOffset();
    }

    // First layout (objects are invisible at this point, so distortion isn't visible)
    layoutToFrame(
      canvas,
      frame,
      videoImg,
      clip,
      vw,
      vh,
      streamType === "screen",
      streamType === "webcam",
      roundedWebcam
    );

    // Force first paint ASAP
    (clip as any).absolutePositioned = true;
    (videoImg as any).clipPath = clip;
    (videoImg as any).dirty = true;
    canvas.renderAll();

    // CRITICAL: Update frame control coordinates after rendering
    // This ensures resize handles are clickable at correct positions with zoomed viewport
    frame.setCoords();

    forceFirstFrameWithImageCapture(videoElement, videoImg, canvas, streamType);
    
    // Smooth fade-in animation after initialization
    // Use longer duration and delay to ensure layout is fully calculated before showing
    const fadeInDelay = 150; // ms delay before starting fade (increased)
    const fadeInDuration = 400; // ms for the actual fade (increased)
    const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);
    
    // Reset mounted flag for this instance
    isMountedRef.current = true;
    
    // Store timeout ref so it can be cancelled on cleanup
    fadeTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted before starting animation
      if (!isMountedRef.current) {
        return;
      }
      
      const fadeStartTime = performance.now();
      
      const fadeIn = (currentTime: number) => {
        // Check if component is still mounted before each frame
        if (!isMountedRef.current) {
          return;
        }
        
        const elapsed = currentTime - fadeStartTime;
        const progress = Math.min(elapsed / fadeInDuration, 1);
        const opacity = easeOutQuad(progress);
        
        videoImg.set({ opacity });
        frame.set({ opacity });
        canvas.requestRenderAll();
        
        if (progress < 1) {
          fadeRafRef.current = requestAnimationFrame(fadeIn);
        } else {
          fadeRafRef.current = null;
          
          // Clear the transitioning flag and snapshot now that fade-in is complete
          store.set(isRatioTransitioningAtom, false);
          store.set(canvasTransitionSnapshotAtom, null);
        }
      };
      
      fadeRafRef.current = requestAnimationFrame(fadeIn);
    }, fadeInDelay);

    // RVFC paint kickers
    let painted = false;
    let rvfcId: number | null = null;
    const markDirtyAndRender = () => {
      if (!painted) painted = true;
      (videoImg as any).dirty = true;
      canvas.renderAll();
    };

    const useRVFC = "requestVideoFrameCallback" in HTMLVideoElement.prototype;
    if (useRVFC) {
      rvfcId = (videoElement as any).requestVideoFrameCallback(() => {
        markDirtyAndRender();
      });
    }

    const onPlayableOnce = () => markDirtyAndRender();
    videoElement.addEventListener("playing", onPlayableOnce, { once: true });
    videoElement.addEventListener("loadeddata", onPlayableOnce, { once: true });
    videoElement.addEventListener("resize", onPlayableOnce, { once: true });

    const t1 = setTimeout(markDirtyAndRender, 120);
    const t2 = setTimeout(markDirtyAndRender, 400);
    const t3 = setTimeout(markDirtyAndRender, 900);

    // Note: Avatar objects are created on-demand via ensureAvatarRenderable()
    // This avoids race conditions and ensures proper timing

    // Keep refs
    videoRef.current = videoImg;
    frameRef.current = frame;
    clipRef.current = clip;
    initRef.current = true;

    // Events
    frame.on("mousedown", (opt: any) => {
      setSelectedStream(streamType);
      setClickedOutside(false);

      canvas.bringObjectToFront(frame);
      ensureWebcamOnTop(canvas);
      opt?.e?.stopPropagation?.();
      interactingRef.current = true;
      scalingSessionRef.current = null;
    });

    frame.on("moving", () => {
      interactingRef.current = true;
      setHasManualModifications(true);
      const w = (frame.width ?? 0) * (frame.scaleX ?? 1);
      const h = (frame.height ?? 0) * (frame.scaleY ?? 1);

      const cW = canvas.getWidth();
      const cH = canvas.getHeight();
      const halfW = w / 2;
      const halfH = h / 2;
      const clampedLeft = Math.min(Math.max(frame.left ?? 0, -halfW), cW - halfW);
      const clampedTop = Math.min(Math.max(frame.top ?? 0, -halfH), cH - halfH);
      if ((frame.left ?? 0) !== clampedLeft || (frame.top ?? 0) !== clampedTop) {
        frame.set({ left: clampedLeft, top: clampedTop });
      }

      const center = frame.getCenterPoint();

      // Get current video dimensions dynamically
      const currentVw = videoElement.videoWidth || vw;
      const currentVh = videoElement.videoHeight || vh;

      // Update videoImg position and ensure it's visible
      videoImg.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        visible: true,
      });

      // keep feedback outline glued if it's active
      if (feedbackOutlineRef.current) {
        if (streamType === "webcam" && roundedWebcamRef.current) {
          showFeedbackOutline(
            canvas,
            "circle",
            center.x,
            center.y,
            Math.min(w, h),
            Math.min(w, h)
          );
        } else {
          showFeedbackOutline(canvas, "rect", center.x, center.y, w, h);
        }
      }

      if (
        streamType === "webcam" &&
        roundedWebcamRef.current &&
        clipRef.current instanceof fabric.Circle
      ) {
        clipRef.current.set({
          radius: Math.min(w, h) / 2,
          left: center.x,
          top: center.y,
          originX: "center",
          originY: "center",
        } as any);
      } else if (clipRef.current instanceof fabric.Rect) {
        const cornerR = getCornerRadius(w, h, /*rounded*/ false);
        clipRef.current.set({
          width: w,
          height: h,
          left: center.x,
          top: center.y,
          originX: "center",
          originY: "center",
          rx: cornerR,
          ry: cornerR,
        } as any);
      }

      (videoImg as any).clipPath = clipRef.current;
      videoImg.setCoords();
      frame.setCoords();
      clipRef.current?.setCoords?.();
      ensureWebcamOnTop(canvas);

      const callback = videoImgLayoutToFrameCallback(
        () => () =>
          layoutToFrame(
            canvas,
            frame,
            videoImg,
            clipRef.current!,
            currentVw,
            currentVh,
            streamType === "screen",
            streamType === "webcam",
            roundedWebcamRef.current,
            true // preserveFrameBounds during moving
          )
      );
      updateGuideVisibility(canvas, frame, callback);
      canvas.requestRenderAll();
    });

    // Preserve stationary corner + circular/square enforcement for rounded webcam
    frame.on("scaling", (opt: any) => {
      if (isRecordingRef.current || showCountdownRef.current) {
        opt?.e?.preventDefault?.();
        opt?.e?.stopPropagation?.();
        return;
      }

      interactingRef.current = true;
      setHasManualModifications(true);

      // Disable guidelines during scaling
      removeAllGuides(canvas);

      const tr = (opt as any)?.transform;
      const corner: string | null = tr?.corner ?? null;

      // Initialize session on first tick
      if (!scalingSessionRef.current) {
        const left = frame.left ?? 0;
        const top = frame.top ?? 0;
        const w = (frame.width ?? 0) * (frame.scaleX ?? 1);
        const h = (frame.height ?? 0) * (frame.scaleY ?? 1);

        scalingSessionRef.current = {
          corner,
          origLeft: left,
          origTop: top,
          origRight: left + w,
          origBottom: top + h,
          origWidth: w,
          origHeight: h,
        };
      }

      const { minW, minH } = getMinSizePx();
      const baseW = frame.width ?? 0;
      const baseH = frame.height ?? 0;

      let newW = baseW * (frame.scaleX ?? 1);
      let newH = baseH * (frame.scaleY ?? 1);

      // If rounded webcam, enforce square intent even during scale (uniform scaling also set)
      if (streamType === "webcam" && roundedWebcamRef.current) {
        const size = Math.max(newW, newH);
        frame.scaleX = size / Math.max(1e-6, baseW);
        frame.scaleY = size / Math.max(1e-6, baseH);
        newW = baseW * (frame.scaleX ?? 1);
        newH = baseH * (frame.scaleY ?? 1);
      }

      const clampedW = Math.max(
        newW,
        streamType === "webcam" && roundedWebcamRef.current
          ? Math.max(minW, minH)
          : minW
      );
      const clampedH = Math.max(
        newH,
        streamType === "webcam" && roundedWebcamRef.current
          ? Math.max(minW, minH)
          : minH
      );
      const clamped = clampedW !== newW || clampedH !== newH;

      if (clamped && scalingSessionRef.current) {
        const sess = scalingSessionRef.current;

        // Determine final box and anchor against the stationary corner
        let left = frame.left ?? 0;
        let top = frame.top ?? 0;

        if (streamType === "webcam" && roundedWebcamRef.current) {
          // ——— Rounded webcam: force square side & circular feedback ———
          const side = Math.max(clampedW, clampedH);
          switch (sess.corner) {
            case "tl":
              left = sess.origRight - side;
              top = sess.origBottom - side;
              break;
            case "tr":
              left = sess.origLeft;
              top = sess.origBottom - side;
              break;
            case "bl":
              left = sess.origRight - side;
              top = sess.origTop;
              break;
            case "br":
            default:
              left = sess.origLeft;
              top = sess.origTop;
              break;
          }
          frame.set({
            left,
            top,
            width: side,
            height: side,
            scaleX: 1,
            scaleY: 1,
          });
          const c = frame.getCenterPoint();
          // Update clip path for rounded webcam
          if (clipRef.current instanceof fabric.Circle) {
            clipRef.current.set({
              radius: side / 2,
              left: c.x,
              top: c.y,
              originX: "center",
              originY: "center",
            } as any);
          } else if (clipRef.current instanceof fabric.Rect) {
            const cornerR = getCornerRadius(
              clampedW,
              clampedH,
              /*rounded*/ false
            );
            clipRef.current.set({
              width: clampedW,
              height: clampedH,
              left: c.x,
              top: c.y,
              originX: "center",
              originY: "center",
              rx: cornerR,
              ry: cornerR,
            } as any);
          }

          showFeedbackOutline(canvas, "circle", c.x, c.y, side, side);
        } else if (streamType === "screen") {
          // ——— Screen share: clamp UNIFORMLY to original frame aspect ———
          const aspect = sess.origWidth / Math.max(1e-6, sess.origHeight);
          // scale factor that satisfies both minW/minH while preserving aspect
          const s = Math.max(
            minW / Math.max(1e-6, sess.origWidth),
            minH / Math.max(1e-6, sess.origHeight)
          );
          const uW = Math.max(newW, sess.origWidth * s);
          const uH = uW / aspect;

          switch (sess.corner) {
            case "tl":
              left = sess.origRight - uW;
              top = sess.origBottom - uH;
              break;
            case "tr":
              left = sess.origLeft;
              top = sess.origBottom - uH;
              break;
            case "bl":
              left = sess.origRight - uW;
              top = sess.origTop;
              break;
            case "br":
            default:
              left = sess.origLeft;
              top = sess.origTop;
              break;
          }
          frame.set({ left, top, width: uW, height: uH, scaleX: 1, scaleY: 1 });
          const c = frame.getCenterPoint();
          showFeedbackOutline(canvas, "rect", c.x, c.y, uW, uH);
        } else {
          // ——— Normal (non-rounded webcam): independent clamp ———
          switch (sess.corner) {
            case "tl":
              left = sess.origRight - clampedW;
              top = sess.origBottom - clampedH;
              break;
            case "tr":
              left = sess.origLeft;
              top = sess.origBottom - clampedH;
              break;
            case "bl":
              left = sess.origRight - clampedW;
              top = sess.origTop;
              break;
            case "br":
            default:
              left = sess.origLeft;
              top = sess.origTop;
              break;
          }
          frame.set({
            left,
            top,
            width: clampedW,
            height: clampedH,
            scaleX: 1,
            scaleY: 1,
          });
          const c = frame.getCenterPoint();
          showFeedbackOutline(canvas, "rect", c.x, c.y, clampedW, clampedH);
        }

        constraintActiveRef.current = true;
      } else {
        // Normal visuals; remove feedback outline
        hideFeedbackOutline(canvas);
        constraintActiveRef.current = false;
      }

      // Get current video dimensions dynamically
      const currentVw = videoElement.videoWidth || vw;
      const currentVh = videoElement.videoHeight || vh;

      // Re-layout video/clip to match frame
      layoutToFrame(
        canvas,
        frame,
        videoImg,
        clipRef.current!,
        currentVw,
        currentVh,
        streamType === "screen",
        streamType === "webcam",
        roundedWebcamRef.current,
        true // preserveFrameBounds during scaling
      );

      // Ensure videoImg is visible and properly positioned after scaling
      const center = frame.getCenterPoint();
      videoImg.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        visible: true,
      });

      videoImg.setCoords();
      frame.setCoords();
      clipRef.current?.setCoords?.();
      ensureWebcamOnTop(canvas);

      const finalW = (frame.width ?? 0) * (frame.scaleX ?? 1);
      const finalH = (frame.height ?? 0) * (frame.scaleY ?? 1);
      const squareTolerance = 10 * getCanvasDisplayScale();
      const isNearSquare = Math.abs(finalW - finalH) < squareTolerance;
      const sqH = guideLinesRef.current.squareH;
      const sqV = guideLinesRef.current.squareV;

      if (isNearSquare && streamType === "webcam" && sqH && sqV) {
        const fLeft = frame.left ?? 0;
        const fTop = frame.top ?? 0;
        const cx = fLeft + finalW / 2;
        const cy = fTop + finalH / 2;
        const ext = Math.max(finalW, finalH) * 0.15;
        sqH.set({ x1: fLeft - ext, y1: cy, x2: fLeft + finalW + ext, y2: cy });
        sqV.set({ x1: cx, y1: fTop - ext, x2: cx, y2: fTop + finalH + ext });
        if (!canvas.getObjects().includes(sqH)) canvas.add(sqH);
        if (!canvas.getObjects().includes(sqV)) canvas.add(sqV);
        canvas.bringObjectToFront(sqH);
        canvas.bringObjectToFront(sqV);
      } else {
        if (sqH && canvas.getObjects().includes(sqH)) canvas.remove(sqH);
        if (sqV && canvas.getObjects().includes(sqV)) canvas.remove(sqV);
      }

      canvas.requestRenderAll();
    });

    frame.on("modified", () => {
      interactingRef.current = false;
      removeAllGuides(canvas);

      // Ensure videoImg is visible and properly positioned
      const center = frame.getCenterPoint();
      videoImg.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        visible: true,
      });
      videoImg.setCoords();
      frame.setCoords(); // CRITICAL: Update frame control positions after modification

      if (
        frame.left !== undefined &&
        frame.top !== undefined &&
        frame.width !== undefined &&
        frame.height !== undefined
      ) {
        // Get backing store scale to convert from Fabric coordinates back to CSS-space
        const backingWidth = canvas.getWidth();
        const cssWidth = canvasWidthRef.current || 960;
        const backingScale = backingWidth / cssWidth;
        const newPosition = {
          x: frame.left / backingScale,
          y: frame.top / backingScale,
          width: (frame.width * (frame.scaleX || 1)) / backingScale,
          height: (frame.height * (frame.scaleY || 1)) / backingScale,
        };
        currentPositionRef.current = newPosition;
        onPositionChange?.(streamType, newPosition);
      }

      constraintActiveRef.current = false;
      scalingSessionRef.current = null;

      hideFeedbackOutline(canvas);

      // Re-enable guidelines after scaling is complete
      const callback = videoImgLayoutToFrameCallback(
        () => () =>
          layoutToFrame(
            canvas,
            frame,
            videoImg,
            clipRef.current!,
            videoElement.videoWidth || 1,
            videoElement.videoHeight || 1,
            streamType === "screen",
            streamType === "webcam",
            roundedWebcamRef.current,
            true // preserveFrameBounds
          )
      );
      updateGuideVisibility(canvas, frame, callback);
    });

    frame.on("mouseup", () => {
      interactingRef.current = false;
      removeAllGuides(canvas);

      // Ensure videoImg is visible and properly positioned
      const center = frame.getCenterPoint();
      videoImg.set({
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        visible: true,
      });
      videoImg.setCoords();
      frame.setCoords(); // CRITICAL: Update frame control positions after mouseup

      // Call updateFrameForRecording with current values instead of captured ones
      // updateFrameForRecording(frame);
      constraintActiveRef.current = false;
      scalingSessionRef.current = null;

      hideFeedbackOutline(canvas);

      // Re-enable guidelines after mouse up (scaling complete)
      const callback = videoImgLayoutToFrameCallback(
        () => () =>
          layoutToFrame(
            canvas,
            frame,
            videoImg,
            clipRef.current!,
            videoElement.videoWidth || 1,
            videoElement.videoHeight || 1,
            streamType === "screen",
            streamType === "webcam",
            roundedWebcamRef.current,
            true // preserveFrameBounds
          )
      );
      updateGuideVisibility(canvas, frame, callback);
    });

    // Keep Fabric video fresh whenever video produces data
    // Skip during hidden tab direct rendering to avoid competition with worker-driven renders
    const markDirty = () => {
      // Skip Fabric.js renders when direct rendering is handling frames
      // This prevents double-rendering and main thread saturation during hidden tab recording
      // CRITICAL: Also check isRecordingRef because the global directRenderActive flag
      // is only set inside the worker tick handler, but video events can fire before it
      if (isDirectRenderActive() || isRecordingRef.current) {
        return;
      }
      (videoImg as any).dirty = true;
      canvas.requestRenderAll();
    };

    const handleResize = () => {
      // Get the actual video stream dimensions (these change on resize)
      const vw = videoElement.videoWidth || 1;
      const vh = videoElement.videoHeight || 1;

      // Get canvas dimensions for bounds checking
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();

      // For screen streams, recalculate frame size based on new video dimensions
      // This is similar to how Preset_ScreenFull works
      const padding = getPadding(canvasRatio);
      const videoAspectRatio = vw / vh;

      // Calculate maximum available space within canvas
      const maxWidth = canvasWidth - padding * 2;
      const maxHeight = canvasHeight - padding * 2;

      // Calculate frame dimensions maintaining video aspect ratio
      let frameWidth = maxWidth;
      let frameHeight = frameWidth / videoAspectRatio;

      // If height exceeds available space, scale down based on height
      if (frameHeight > maxHeight) {
        frameHeight = maxHeight;
        frameWidth = frameHeight * videoAspectRatio;
      }

      // Center the frame within the canvas
      const newLeft = padding + (maxWidth - frameWidth) / 2;
      const newTop = padding + (maxHeight - frameHeight) / 2;

      // frame.set({
      //   width: frameWidth,
      //   height: frameHeight,
      //   scaleX: 1,
      //   scaleY: 1,
      //   left: newLeft,
      //   top: newTop,
      //   lockUniScaling:
      //     streamType === "screen" ||
      //     (streamType === "webcam" && roundedWebcamRef.current),
      // });
      //frame.setCoords();

      // Update videoImg to use the new video element with correct dimensions
      videoImg.setElement(videoElement);
      videoImg.dirty = true;

      // Use layoutToFrame to properly handle video scaling and positioning
      // Use "fill" for screen (matches frame), "cover" for webcam (maintains aspect ratio)
      layoutToFrame(
        canvas,
        frame,
        videoImg,
        clipRef.current!,
        vw,
        vh,
        streamType === "screen",
        streamType === "webcam",
        roundedWebcamRef.current,
        true, // preserveFrameBounds during resize
        streamType === "screen" ? "fill" : "cover" // webcam must maintain aspect ratio
      );

      // Ensure webcam is on top
      ensureWebcamOnTop(canvas);

      // Skip Fabric.js render during recording - vimeoDirectRender handles it
      // The layout update above (layoutToFrame) still runs to update object positions
      if (!isDirectRenderActive() && !isRecordingRef.current) {
        canvas.requestRenderAll();
      }
      markDirty();
    };
    videoElement.addEventListener("timeupdate", markDirty);
    videoElement.addEventListener("seeked", markDirty);
    videoElement.addEventListener("resize", handleResize);

    registerVideoObject(videoImg);

    // Cleanup for listeners & timers when canvas objects are torn down
    const detach = () => {
      videoElement.removeEventListener("playing", onPlayableOnce);
      videoElement.removeEventListener("loadeddata", onPlayableOnce);
      videoElement.removeEventListener("resize", onPlayableOnce);
      videoElement.removeEventListener("timeupdate", markDirty);
      videoElement.removeEventListener("seeked", markDirty);
      videoElement.removeEventListener("resize", handleResize);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (rvfcId != null && (videoElement as any).cancelVideoFrameCallback) {
        try {
          (videoElement as any).cancelVideoFrameCallback(rvfcId);
        } catch {}
      }
    };

    (frame as any).__firstPaintDetach__ = detach;
  }, [
    canvasReady,
    stageRef,
    videoElement,
    streamType,
    isSelected,
    roundedWebcam,
    getAdjustedControlsSize,
    getCornerSize,
    getBorderScaleFactor,
    onPositionChange,
    registerVideoObject,
    removeAllGuides,
    updateGuideVisibility,
    updateFrameForRecording,
    initialX,
    initialY,
    initialWidth,
    initialHeight,
    getMinSizePx,
  ]);

  // Wait for video to be ready, then init (and re-try if intrinsic size "resizes")
  useEffect(() => {
    if (!videoElement) return;

    if (
      videoElement.readyState >= HTMLMediaElement.HAVE_METADATA &&
      videoElement.videoWidth > 0 &&
      videoElement.videoHeight > 0
    ) {
      initCanvasObjects();
    }

    const onReady = () => initCanvasObjects();

    videoElement.addEventListener("loadedmetadata", onReady, { once: true });
    videoElement.addEventListener("resize", onReady, { once: true });

    // Kick playback; helps some browsers populate dimensions
    videoElement.muted = true;
    (videoElement as any).playsInline = true;
    videoElement.play().catch(() => {});

    return () => {
      videoElement.removeEventListener("loadedmetadata", onReady);
      videoElement.removeEventListener("resize", onReady);
    };
  }, [videoElement, initCanvasObjects]);

  // Detect canvas instance swap and re-init
  useEffect(() => {
    const currentCanvas: any | undefined = stageRef?.current?.getCanvas?.();
    if (!currentCanvas) return;

    if (prevCanvasRef.current !== currentCanvas) {
      const prevFrame = frameRef.current as any;
      if (prevFrame && prevFrame.__firstPaintDetach__) {
        try {
          prevFrame.__firstPaintDetach__();
        } catch {}
      }
      cleanupCanvasObjects();
      initOnce.current = false;
      initRef.current = false;

      if (
        videoElement &&
        videoElement.readyState >= HTMLMediaElement.HAVE_METADATA &&
        videoElement.videoWidth > 0 &&
        videoElement.videoHeight > 0
      ) {
        initCanvasObjects();
      }
    }

    prevCanvasRef.current = currentCanvas;
  }, [
    stageRef,
    canvasReady,
    // Note: recordingQuality removed - we only care about canvas instance changes
    // canvasWidth/canvasHeight changes don't require reinit if canvas instance is same
    canvasRatio,
    videoElement,
    cleanupCanvasObjects,
    initCanvasObjects,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const prevFrame = frameRef.current as any;
      if (prevFrame && prevFrame.__firstPaintDetach__) {
        try {
          prevFrame.__firstPaintDetach__();
        } catch {}
      }
      cleanupCanvasObjects();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear current position when initial props change (preset applied)
  // Reset position to new values when NOT in manual mode (i.e., when a preset is applied)
  useEffect(() => {
    if (!isManualPreset) {
      currentPositionRef.current = null;
      onPositionChange?.(streamType, {
        x: initialX,
        y: initialY,
        width: initialWidth,
        height: initialHeight,
      });
    }
  }, [
    initialX,
    initialY,
    initialWidth,
    initialHeight,
    isManualPreset,
    onPositionChange,
    streamType,
  ]);

  // Only stroke updates when isSelected changes
  useEffect(() => {
    const frame = frameRef.current;
    const canvas = stageRef?.current?.getCanvas?.();
    if (!frame || !canvas) return;
    
    const actualCornerSize = getCornerSize();
    const isDuringRecording = isRecordingRef.current || showCountdownRef.current;
    
    // CRITICAL: Completely hide controls during recording - no resize allowed
    if (isDuringRecording) {
      frame.setControlsVisibility({
        mt: false, mb: false, ml: false, mr: false,
        tl: false, tr: false, bl: false, br: false, mtr: false,
      });
      frame.set({
        selectable: false, // CRITICAL: Disable selection during recording
        evented: false, // CRITICAL: Disable events during recording
        hasBorders: false,
        hasControls: false,
        borderColor: "transparent",
        cornerColor: "transparent",
        cornerStrokeColor: "transparent",
        transparentCorners: true,
        cornerSize: 0,
        fill: "transparent",
        visible: false,
      });
    } else {
      const isScreen = streamType === "screen";
      frame.setControlsVisibility({
        mt: !isScreen, mb: !isScreen, ml: !isScreen, mr: !isScreen,
        tl: true, tr: true, bl: true, br: true, mtr: false,
      });
      frame.set({
        selectable: true,
        evented: true,
        hasBorders: isSelected,
        hasControls: true,
        borderColor: isSelected ? guideColor : "transparent",
        cornerColor: isSelected ? "white" : "transparent",
        cornerStrokeColor: isSelected ? guideColor : "transparent",
        transparentCorners: !isSelected,
        cornerSize: actualCornerSize,
        fill: "rgba(0,0,0,0.01)",
        visible: true,
      });
    }
    frame.setCoords();
    ensureWebcamOnTop(canvas);
    canvas.requestRenderAll();
  }, [isSelected, isRecording, showCountdown, stageRef, getCornerSize, streamType]);

  // Relayout on any ratio change (but NOT on dimension-only changes)
  // Use useLayoutEffect only for critical ratio changes, useEffect for others
  useLayoutEffect(() => {
    if (!stageRef?.current) return;

    if (!initRef.current) {
      lastRatioRef.current = canvasRatio as CanvasRatio;
      lastCanvasDimensionsRef.current = {
        width: canvasWidth,
        height: canvasHeight,
      };
      return;
    }

    const ratioChanged = lastRatioRef.current !== canvasRatio;

    // Only reset layout if ratio actually changed, not just dimensions
    if (ratioChanged) {
      currentPositionRef.current = null;

      const canvas = stageRef.current.getCanvas() as any;
      const frame = frameRef.current;
      const videoImg = videoRef.current;
      const clip = clipRef.current;

      if (
        frame &&
        videoImg &&
        clip &&
        videoElement?.videoWidth &&
        videoElement?.videoHeight
      ) {
        // Use opacity-based transition to avoid distortion during ratio change
        // Fade out -> snap to new dimensions -> fade in
        const wasRenderOnAddRemove = canvas.renderOnAddRemove;
        canvas.renderOnAddRemove = false;

        const fadeOutDuration = 120; // ms
        const fadeInDuration = 180; // ms
        const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);
        const easeInQuad = (t: number) => t * t;

        const animateTransition = () => {
          const startTime = performance.now();

          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const totalDuration = fadeOutDuration + fadeInDuration;
            
            if (elapsed < fadeOutDuration) {
              // Phase 1: Fade out
              const fadeProgress = elapsed / fadeOutDuration;
              const opacity = 1 - easeOutQuad(fadeProgress);
              videoImg.set({ opacity });
              frame.set({ opacity });
              canvas.requestRenderAll();
              requestAnimationFrame(animate);
            } else if (elapsed < totalDuration) {
              // Phase 2: Snap to new position/size and fade in
              const fadeInElapsed = elapsed - fadeOutDuration;
              const fadeProgress = fadeInElapsed / fadeInDuration;
              const opacity = easeInQuad(fadeProgress);

              // On first frame of fade-in, snap to final dimensions
              if (fadeInElapsed < 20) {
                frame.set({
                  left: initialX,
                  top: initialY,
                  width: initialWidth,
                  height: initialHeight,
                  scaleX: 1,
                  scaleY: 1,
                  lockUniScaling:
                    streamType === "screen" ||
                    (streamType === "webcam" && roundedWebcam),
                });
                (frame as any).cornerSize = getCornerSize();
                frame.setCoords();

                layoutToFrame(
                  canvas,
                  frame,
                  videoImg,
                  clip,
                  videoElement.videoWidth,
                  videoElement.videoHeight,
                  streamType === "screen",
                  streamType === "webcam",
                  roundedWebcam
                );
                ensureWebcamOnTop(canvas);
              }

              videoImg.set({ opacity });
              frame.set({ opacity });
              canvas.requestRenderAll();
              requestAnimationFrame(animate);
            } else {
              // Animation complete - ensure full opacity and restore rendering
              videoImg.set({ opacity: 1 });
              frame.set({ opacity: 1 });
              canvas.renderOnAddRemove = wasRenderOnAddRemove;
              canvas.requestRenderAll();
            }
          };

          requestAnimationFrame(animate);
        };

        // Start animation
        requestAnimationFrame(animateTransition);
      }

      lastCanvasDimensionsRef.current = {
        width: canvasWidth,
        height: canvasHeight,
      };
    }

    lastRatioRef.current = canvasRatio as CanvasRatio;
  }, [
    canvasRatio, // Only depend on ratio change, not dimensions
    stageRef,
    initialX,
    initialY,
    initialWidth,
    initialHeight,
    roundedWebcam,
    streamType,
    videoElement,
    getCornerSize,
  ]);

  // Track previous initial dimensions AND canvas dimensions for quality change detection
  const prevInitialDimensionsRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const prevCanvasDimsForQualityRef = useRef<{ width: number; height: number } | null>(null);

  // Handle quality changes (dimension-only, no ratio change)
  // This scales existing Fabric.js objects when initialWidth/initialHeight change DUE TO quality change
  useEffect(() => {
    const prev = prevInitialDimensionsRef.current;
    const prevCanvas = prevCanvasDimsForQualityRef.current;
    
    // Helper to update refs - call this before any return
    const updateRefs = () => {
      prevInitialDimensionsRef.current = {
        x: initialX,
        y: initialY,
        width: initialWidth,
        height: initialHeight,
      };
      prevCanvasDimsForQualityRef.current = {
        width: canvasWidth,
        height: canvasHeight,
      };
    };
    
    if (!initRef.current || !stageRef?.current) {
      updateRefs();
      return;
    }

    const frame = frameRef.current;
    const videoImg = videoRef.current;
    const clip = clipRef.current;
    const canvas = stageRef.current.getCanvas?.();
    
    if (!frame || !videoImg || !canvas) {
      updateRefs();
      return;
    }
    
    // QUALITY CHANGE detection: Canvas dimensions must have changed
    // User drag/resize only changes object dimensions, not canvas dimensions
    const canvasDimensionsChanged = prevCanvas && (
      prevCanvas.width !== canvasWidth || 
      prevCanvas.height !== canvasHeight
    );
    
    // Only process if CANVAS dimensions changed (quality change)
    // This distinguishes quality change from user drag/resize
    if (canvasDimensionsChanged) {
      const ratioChanged = lastRatioRef.current !== canvasRatio;
      
      // Only handle dimension changes when ratio hasn't changed (quality change)
      if (!ratioChanged && prev && prev.width > 0 && prev.height > 0) {
        // Simply update the frame position and size to match new initial values
        frame.set({
          left: initialX,
          top: initialY,
          width: initialWidth,
          height: initialHeight,
          scaleX: 1,
          scaleY: 1,
        });
        frame.setCoords();
        
        // Update the clip shape (rect or circle) to match new frame dimensions
        if (clip) {
          if (roundedWebcam && streamType === 'webcam') {
            // Circle clip for rounded webcam
            clip.set({
              radius: Math.min(initialWidth, initialHeight) / 2,
              left: initialWidth / 2,
              top: initialHeight / 2,
            });
          } else {
            // Rect clip for regular streams
            clip.set({
              width: initialWidth,
              height: initialHeight,
              left: 0,
              top: 0,
            });
          }
        }
        
        // Update the video image element to fill the frame
        if (videoElement?.videoWidth && videoElement?.videoHeight) {
          const vw = videoElement.videoWidth;
          const vh = videoElement.videoHeight;
          const videoAspect = vw / vh;
          const frameAspect = initialWidth / initialHeight;
          
          let scale: number;
          let offsetX = 0;
          let offsetY = 0;
          
          // Cover fit - fill the frame completely, cropping if needed
          if (videoAspect > frameAspect) {
            // Video is wider - scale to fit height, crop sides
            scale = initialHeight / vh;
            offsetX = (initialWidth - vw * scale) / 2;
          } else {
            // Video is taller - scale to fit width, crop top/bottom
            scale = initialWidth / vw;
            offsetY = (initialHeight - vh * scale) / 2;
          }
          
          videoImg.set({
            scaleX: (streamType === 'webcam' && mirroredWebcam) ? -scale : scale,
            scaleY: scale,
            left: (streamType === 'webcam' && mirroredWebcam) ? initialWidth - offsetX : offsetX,
            top: offsetY,
          });
        }
        
        // Update current position ref
        currentPositionRef.current = {
          x: initialX,
          y: initialY,
          width: initialWidth,
          height: initialHeight,
        };
        
        // Ensure webcam stays on top
        ensureWebcamOnTop(canvas);
        canvas.requestRenderAll();
      }
    }
    
    // Always update the refs
    updateRefs();
  }, [initialX, initialY, initialWidth, initialHeight, canvasWidth, canvasHeight, canvasRatio, stageRef, roundedWebcam, streamType, mirroredWebcam, videoElement]);

  // Keep frame controls in sync with recording state / countdown
  useEffect(() => {
    isRecordingRef.current = isRecording;
    // CRITICAL: Always call updateFrameForRecording when recording state changes
    // This ensures controls are hidden when recording starts AND restored when recording stops
    if (frameRef.current) {
      updateFrameForRecording(frameRef.current);
      // Force canvas re-render to apply changes
      if (stageRef?.current) {
        const canvas = stageRef.current.getCanvas();
        if (canvas) {
          canvas.requestRenderAll();
        }
      }
    }
  }, [isRecording, updateFrameForRecording, stageRef]);

  // Keep frame controls in sync with recording state / countdown
  useEffect(() => {
    clickedOutsideRef.current = clickedOutside;
    if (clickedOutside && frameRef.current) {
      updateFrameForRecording(frameRef.current);
      // Force canvas re-render to show the deselected state
      if (stageRef?.current) {
        const canvas = stageRef.current.getCanvas();
        if (canvas) {
          canvas.requestRenderAll();
        }
      }
    }
  }, [clickedOutside, isRecording, updateFrameForRecording, stageRef]);

  useEffect(() => {
    if (selectedStream === streamType && !clickedOutside && frameRef.current) {
      updateFrameForRecording(frameRef.current);
    }
  }, [
    isSelected,
    selectedStream,
    streamType,
    updateFrameForRecording,
    clickedOutside,
  ]);

  useEffect(() => {
    showCountdownRef.current = showCountdown;
    // CRITICAL: Always call updateFrameForRecording when countdown state changes
    // This ensures controls are hidden during countdown AND restored when countdown ends
    if (frameRef.current) {
      updateFrameForRecording(frameRef.current);
      // Force canvas re-render to apply changes
      if (stageRef?.current) {
        const canvas = stageRef.current.getCanvas();
        if (canvas) {
          canvas.requestRenderAll();
        }
      }
    }
  }, [showCountdown, updateFrameForRecording, stageRef]);

  // Update rounded corners when roundedWebcam toggles: swap clip type & lock scaling
  useEffect(() => {
    if (!initRef.current || !stageRef?.current || streamType !== "webcam")
      return;

    const canvas = stageRef.current.getCanvas() as any;
    const frame = frameRef.current!;
    const videoImg = videoRef.current!;
    const prevClip = clipRef.current;

    let w = (frame.width ?? 0) * (frame.scaleX ?? 1);
    let h = (frame.height ?? 0) * (frame.scaleY ?? 1);

    // For rounded webcam, make the frame square to ensure proper circular clipping
    // Always make it square when toggling TO rounded webcam
    if (roundedWebcam) {
      const minDimension = Math.min(w, h);
      w = minDimension;
      h = minDimension;

      // Store the current center position before resizing
      const currentCenter = frame.getCenterPoint();

      // Calculate new position to maintain the center
      const newLeft = currentCenter.x - w / 2;
      const newTop = currentCenter.y - h / 2;

      // Update frame dimensions to be square while preserving center position
      frame.set({
        width: w,
        height: h,
        scaleX: 1,
        scaleY: 1,
        left: newLeft,
        top: newTop,
      });
    }

    const center = frame.getCenterPoint();

    // Update frame scaling lock
    frame.set({ lockUniScaling: roundedWebcam });
    frame.setCoords();

    // Build the appropriate new clip
    let newClip: any;
    if (roundedWebcam) {
      const circle = new fabric.Circle({
        radius: Math.min(w, h) / 2,
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
      });
      newClip = circle;
    } else {
      const cornerR = getCornerRadius(w, h, /* rounded */ false);
      const rect = new fabric.Rect({
        width: w,
        height: h,
        left: center.x,
        top: center.y,
        originX: "center",
        originY: "center",
        rx: cornerR,
        ry: cornerR,
      });
      newClip = rect;
    }

    // Make new clip non-interactive and invisible
    // FIX: Do NOT add clip to canvas - it only needs to be set as clipPath
    newClip.set({
      selectable: false,
      evented: false,
      visible: false,
      excludeFromExport: true,
    });
    (newClip as any).absolutePositioned = true;
    (videoImg as any).clipPath = newClip;
    clipRef.current = newClip;

    // Force re-layout to ensure proper video scaling
    const vw = videoElement?.videoWidth || 1;
    const vh = videoElement?.videoHeight || 1;

    layoutToFrame(
      canvas,
      frame,
      videoImg,
      newClip,
      vw,
      vh,
      false, // isScreen
      true, // isWebcam
      roundedWebcam
    );

    // Force re-render
    videoImg.setCoords();
    newClip.setCoords();
    canvas.requestRenderAll();

    // Update the ref with the current value
    roundedWebcamRef.current = roundedWebcam;
  }, [roundedWebcam, stageRef, streamType, videoElement]);

  // Apply mirror effect when mirroredWebcam changes (webcam only)
  useEffect(() => {
    if (!initRef.current || !stageRef?.current || streamType !== "webcam")
      return;

    const canvas = stageRef.current.getCanvas() as any;
    const videoImg = videoRef.current!;

    // Apply flipX to webcam video image
    videoImg.set({ flipX: mirroredWebcam });
    videoImg.setCoords();
    canvas.requestRenderAll();

    // Update the ref with the current value
    mirroredWebcamRef.current = mirroredWebcam;
  }, [mirroredWebcam, stageRef, streamType]);

  // Scale positions proportionally when canvas dimensions change (e.g., quality change)
  // Use useEffect since this doesn't need to run before paint
  useEffect(() => {
    if (!initRef.current || !stageRef?.current || !frameRef.current) return;

    const lastDimensions = lastCanvasDimensionsRef.current;

    // If this is the first render or ratio changed, just update the ref
    if (
      !lastDimensions ||
      lastRatioARef.current !== canvasRatio ||
      lastDimensions.width === canvasWidth ||
      lastDimensions.height === canvasHeight ||
      lastDimensions.width === canvasHeight
    ) {
      lastRatioARef.current = canvasRatio as CanvasRatio;
      lastCanvasDimensionsRef.current = {
        width: canvasWidth,
        height: canvasHeight,
      };
      return;
    }

    // Check if dimensions changed but ratio stayed the same
    const dimensionsChanged =
      lastDimensions.width !== canvasWidth ||
      lastDimensions.height !== canvasHeight;

    if (dimensionsChanged && lastRatioRef.current === canvasRatio) {
      // Canvas dimensions changed (likely due to quality change) - scale position proportionally
      const scaleX = canvasWidth / lastDimensions.width;
      const scaleY = canvasHeight / lastDimensions.height;

      const frame = frameRef.current;
      const videoImg = videoRef.current;
      const clip = clipRef.current;
      const canvas = stageRef.current.getCanvas() as any;

      if (frame && videoImg && clip) {
        // Disable rendering to batch all changes atomically
        const wasRenderOnAddRemove = canvas.renderOnAddRemove;
        canvas.renderOnAddRemove = false;

        // Scale the frame position and size
        const currentLeft = frame.left ?? 0;
        const currentTop = frame.top ?? 0;
        const currentWidth = (frame.width ?? 0) * (frame.scaleX ?? 1);
        const currentHeight = (frame.height ?? 0) * (frame.scaleY ?? 1);

        const newLeft = currentLeft * scaleX;
        const newTop = currentTop * scaleY;
        const newWidth = currentWidth * scaleX;
        const newHeight = currentHeight * scaleY;

        frame.set({
          left: newLeft,
          top: newTop,
          width: newWidth,
          height: newHeight,
          scaleX: 1,
          scaleY: 1,
        });

        frame.setCoords();

        // Re-layout video/clip to match new frame dimensions
        if (videoElement?.videoWidth && videoElement?.videoHeight) {
          layoutToFrame(
            canvas,
            frame,
            videoImg,
            clip,
            videoElement.videoWidth,
            videoElement.videoHeight,
            streamType === "screen",
            streamType === "webcam",
            roundedWebcam
          );
        }

        // Update stored position - convert from backing store coordinates to CSS-space
        const backingWidth = canvas.getWidth();
        const cssWidth = canvasWidthRef.current || 960;
        const backingScale = backingWidth / cssWidth;
        const newPosition = {
          x: newLeft / backingScale,
          y: newTop / backingScale,
          width: newWidth / backingScale,
          height: newHeight / backingScale,
        };
        currentPositionRef.current = newPosition;
        onPositionChange?.(streamType, newPosition);

        // Restore rendering and render once
        canvas.renderOnAddRemove = wasRenderOnAddRemove;
        canvas.requestRenderAll();
      }
    }

    // Update the ref
    lastCanvasDimensionsRef.current = {
      width: canvasWidth,
      height: canvasHeight,
    };
  }, [
    canvasWidth,
    canvasHeight,
    canvasRatio,
    stageRef,
    videoElement,
    streamType,
    roundedWebcam,
    onPositionChange,
    layoutToFrame,
  ]);

  // Update guideline positions when canvas dimensions change
  useEffect(() => {
    if (!initRef.current || !stageRef?.current) return;

    const canvas = stageRef.current.getCanvas() as any;

    // Update all guide positions to match new canvas dimensions
    updateAllGuidePositions(canvas);
  }, [
    canvasWidth,
    canvasHeight,
    canvasRatio,
    stageRef,
    updateAllGuidePositions,
  ]);

  useEffect(() => {
    // Only handle avatar for webcam streams
    if (!initRef.current || !stageRef?.current || streamType !== "webcam") {
      return;
    }

    const videoImg = videoRef.current;
    if (!videoImg) return;

    // Check if we actually need to update the source
    const avatarWebcamChanged = lastAvatarWebcamRef.current !== avatarWebcam;
    const avatarImageChanged = lastAvatarImageRef.current !== avatarImage;

    // Only update if something actually changed
    if (!avatarWebcamChanged && !avatarImageChanged) {
      return;
    }

    const updateVideoSource = async () => {
      const canvas = stageRef.current.getCanvas() as any;
      const frame = frameRef.current;
      const clip = clipRef.current;

      if (avatarWebcam && avatarImage) {
        // Switch to avatar

        videoImg.setElement(avatarImage);
        videoImg.dirty = true;
        videoImg.set({ flipX: false }); // Avatar doesn't need mirroring

        // Use object-fit: cover behavior for avatar images
        if (frame && clip) {
          const frameWidth = (frame.width ?? 0) * (frame.scaleX ?? 1);
          const frameHeight = (frame.height ?? 0) * (frame.scaleY ?? 1);
          const center = frame.getCenterPoint();

          // Get avatar dimensions
          const avatarWidth = avatarImage.width || 1;
          const avatarHeight = avatarImage.height || 1;

          // Use cover behavior to maintain aspect ratio (object-fit: cover)
          const scale = Math.max(
            frameWidth / avatarWidth,
            frameHeight / avatarHeight
          );

          videoImg.set({
            left: center.x,
            top: center.y,
            originX: "center",
            originY: "center",
            scaleX: scale,
            scaleY: scale,
          });

          videoImg.setCoords();
        }
      } else {
        // Switch back to webcam
        videoImg.setElement(videoElement);
        videoImg.dirty = true;
        videoImg.set({ flipX: mirroredWebcam }); // Restore mirror setting

        // Re-layout webcam video with cover behavior
        if (frame && clip) {
          const vw = videoElement?.videoWidth || 1;
          const vh = videoElement?.videoHeight || 1;
          layoutToFrame(
            canvas,
            frame,
            videoImg,
            clip,
            vw,
            vh,
            false, // isScreen
            true, // isWebcam
            roundedWebcamRef.current,
            true // preserveFrameBounds during avatar/webcam toggle
          );
        }
      }

      canvas.requestRenderAll();
      // Update refs to track current state
      lastAvatarWebcamRef.current = avatarWebcam;
      lastAvatarImageRef.current = avatarImage;
    };

    updateVideoSource();
  }, [
    avatarWebcam,
    streamType,
    stageRef,
    videoElement,
    mirroredWebcam,
    avatarImage,
  ]);

  // Update avatar when user data changes
  useEffect(() => {
    if (!initRef.current || !stageRef?.current || streamType !== "webcam")
      return;

    const updateAvatarImage = async () => {
      const fallbackUrl =
        "https://i.postimg.cc/SQvP9c0S/Chat-GPT-Image-Oct-5-2025-12-30-31-PM.png";
      const avatarUrl = userData?.imageThumbnailUrl || fallbackUrl;

      if (avatarUrl) {
        try {
          // Preload the new avatar image in context
          await preloadAvatarImage(avatarUrl);
        } catch (error) {
          console.error("Failed to load new avatar image:", error);
        }
      }
    };

    updateAvatarImage();
  }, [userData?.imageThumbnailUrl, streamType, stageRef, preloadAvatarImage]);

  return null;
};

export default React.memo(DraggableResizableStreamImpl, (prev, next) => {
  return (
    prev.isSelected === next.isSelected &&
    prev.streamType === next.streamType &&
    prev.videoElement === next.videoElement &&
    prev.onPositionChange === next.onPositionChange &&
    prev.initialX === next.initialX &&
    prev.initialY === next.initialY &&
    prev.initialWidth === next.initialWidth &&
    prev.initialHeight === next.initialHeight &&
    prev.isManualPreset === next.isManualPreset &&
    prev.getCurrentLayout === next.getCurrentLayout
  );
});
